import { useSelector } from "react-redux";
import { User, UserGroup } from "../../common/User";
import { selectCurrentUser } from "../../redux/authentication/authSlice";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  LinearProgress,
  MenuItem,
  OutlinedInput,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  Badge,
  TextField,
  Tooltip,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useGetUsersByGroupQuery } from "../../redux/api/user";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import {
  DisabledJobRatings,
  JobRating,
  JobRatingLabel,
} from "../../common/JobOrder";
import { MarinerProfile } from "../../common/MarinerProfile";
import {
  trainingOrderSearchParams,
  TrainingOrder,
} from "../../common/Training";
import MarinerProfileAutocomplete from "../marinerProfile/MarinerProfileAutocomplete";
import { useGetTrainingOrdersQuery } from "../../redux/api/trainingOrder";
import { colorFromName } from "../../common/ColorFromName";
import { formatDateString } from "../../common/FormatDateString";
import moment from "moment";
import useDocumentTitle from "../../util/useDocumentTitle";

type order = {
  id: number;
  dueDate: number;
  startDate: number;
  endDate: number;
  vessel: string;
  assignee: User;
  rating: string;
  trainingName: string;
  mariner: string;
  marinerId: number | undefined;
  completed: number;
  notApplicable: number;
  tasks: number;
  critical: boolean;
  requestByCaptain: boolean;
  modifiedByCaptain: boolean;
};

const TrainingOrderListComponent = () => {
  useDocumentTitle("Training Orders");
  const user = useSelector(selectCurrentUser);
  const isViewOnly =
    (user?.userGroup as UserGroup) === UserGroup.HUMAN_RESOURCES;
  const canCreate =
    (user?.userGroup as UserGroup) === UserGroup.CREWING_MANAGERS ||
    (user?.userGroup as UserGroup) === UserGroup.HUMAN_RESOURCES_ADMINS ||
    (user?.userGroup as UserGroup) === UserGroup.CREWING_SPECIALISTS ||
    (user?.userGroup as UserGroup) === UserGroup.CREWING_EVAL_SPECIALISTS ||
    (user?.userGroup as UserGroup) === UserGroup.CREWING_EXECS;
  const [trainingOrderSearchParams, setTrainingOrderSearchParams] =
    useState<trainingOrderSearchParams>({
      assigneeId: "",
      rating: undefined,
      compatibleWithRating: undefined,
      trainingOrderId: "",
      scheduled: false,
      completed: false,
      deleted: false,
      requestedByCaptain: undefined,
      modifiedByCaptain: undefined,
      profileId: undefined,
      page: 0,
      size: 15,
      sortColumn: "dueDate",
      sortDirection: "asc",
    });
  const { data: specialists, isLoading: isSpecialistsLoading } =
    useGetUsersByGroupQuery(UserGroup.CREWING_SPECIALISTS);
  const { data: managers, isLoading: isManagersLoading } =
    useGetUsersByGroupQuery(UserGroup.CREWING_MANAGERS);
  const { data: evalSpecialists, isLoading: isEvalSpecialistsLoading } =
    useGetUsersByGroupQuery(UserGroup.CREWING_EVAL_SPECIALISTS);
  const assignees = useMemo(() => {
    return [...(specialists || []), ...(managers || []), ...(evalSpecialists || [])];
  }, [specialists, managers, evalSpecialists]);

  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [assignee, setAssignee] = useState<User | null>(null);
  const [rating, setRating] = useState<JobRating | null>(null);
  const [profile, setProfile] = useState<MarinerProfile | null>(null);
  const [status, setStatus] = useState<string[]>([]);
  const [skipQuery] = useState(Array.from(searchParams).length > 0);
  const navigate = useNavigate();
  const redirectToTrainingOrder = () => {
    const newTrainingOrderId = "new";
    navigate(`/trainingOrders/${newTrainingOrderId}`);
  };

  const {
    data: trainingOrdersFromApi,
    isLoading: isTrainingOrdersLoading,
    refetch,
  } = useGetTrainingOrdersQuery(trainingOrderSearchParams, {
    skip: skipQuery,
    refetchOnMountOrArgChange: true,
  });

  // Query for requested training orders count (for notification dot)
  const {
    data: requestedOrdersData,
    refetch: refetchRequestedOrders
  } = useGetTrainingOrdersQuery(
    {
      assigneeId: "",
      rating: undefined,
      compatibleWithRating: undefined,
      trainingOrderId: "",
      scheduled: undefined,
      completed: undefined,
      deleted: false,
      requestedByCaptain: true,
      modifiedByCaptain: undefined,
      profileId: undefined,
      page: 0,
      size: 1,
    },
    { skip: false }
  );
  const requestedOrdersCount = requestedOrdersData?.totalElements || 0;

  // Refetch requested orders when navigating to this page
  useEffect(() => {
    refetchRequestedOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  let trainingOrders: order[] = (
    trainingOrdersFromApi?.content as TrainingOrder[]
  )?.map((order: any) => {
    return {
      id: order.id,
      dueDate: order.dueDate,
      startDate: order.startDate,
      endDate: order.endDate,
      vessel: order.vessel?.name,
      assignee: order.assignee,
      rating: order.rating,
      port: order.port,
      trainingName: order.training?.name,
      mariner: !!order.mariner
        ? order.mariner?.firstName + " " + order.mariner?.lastName
        : "",
      marinerId: order.mariner?.id,
      completed: order.checklistRoot?.subTasksCompletedCount | 0,
      notApplicable: order.checklistRoot?.subTasksNotApplicableCount | 0,
      tasks: order.checklistRoot?.subTasksCount | 0,
      critical:
        moment(new Date(order.dueDate).toISOString()).diff(
          moment(new Date()),
          "days"
        ) < 7
          ? true
          : false,
      requestByCaptain: order.requestByCaptain,
      modifiedByCaptain: order.modifiedByCaptain,
    };
  });

  const resetTrainingOrderSearchParams = () => {
    setTrainingOrderSearchParams({
      assigneeId: "",
      rating: undefined,
      compatibleWithRating: undefined,
      trainingOrderId: "",
      scheduled: false,
      completed: false,
      deleted: false,
      requestedByCaptain: false,
      modifiedByCaptain: undefined,
      profileId: undefined,
      page: 0,
      size: 15,
      sortColumn: "dueDate",
      sortDirection: "asc",
    });
  };

  useEffect(() => {
    setTrainingOrderSearchParams({
      ...trainingOrderSearchParams,
      assigneeId: assignee?.id ? `${assignee.id}` : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignee]);
  // STATUS
  useEffect(() => {
    setTrainingOrderSearchParams({
      ...trainingOrderSearchParams,
      modifiedByCaptain: status.includes("modified") ? true : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);
  useEffect(() => {
    setTrainingOrderSearchParams({
      ...trainingOrderSearchParams,
      profileId: !!profile?.id ? `${profile.id}` : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);
  useEffect(() => {
    setTrainingOrderSearchParams({
      ...trainingOrderSearchParams,
      rating: rating ? rating : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rating]);

  type TabValue =
    | "requestedTrainingOrders"
    | "activeTrainingOrders"
    | "scheduledTrainingOrders"
    | "completedTrainingOrders"
    | "deletedTrainingOrders";
  const [currentTab, setCurrentTab] = useState<TabValue>(
    "activeTrainingOrders"
  );

  const handleSort =
    (property: keyof TrainingOrder) => (event: React.MouseEvent<unknown>) => {
      const direction =
        trainingOrderSearchParams.sortColumn === property &&
          trainingOrderSearchParams.sortDirection === "asc"
          ? "desc"
          : "asc";
      setTrainingOrderSearchParams({
        ...trainingOrderSearchParams,
        sortColumn: property,
        sortDirection: direction,
      });

      refetch();
    };

  const handleActiveTabToggle = (newTab: TabValue) => {
    switch (newTab) {
      case "activeTrainingOrders": {
        setTrainingOrderSearchParams({
          assigneeId: "",
          rating: undefined,
          compatibleWithRating: undefined,
          trainingOrderId: "",
          scheduled: false,
          completed: false,
          deleted: false,
          requestedByCaptain: undefined,
          modifiedByCaptain: undefined,
          profileId: undefined,
          page: 0,
          size: 15,
          sortColumn: "dueDate",
          sortDirection: "asc",
        });
        break;
      }
      case "scheduledTrainingOrders": {
        setTrainingOrderSearchParams({
          assigneeId: "",
          rating: undefined,
          compatibleWithRating: undefined,
          trainingOrderId: "",
          scheduled: true,
          completed: false,
          deleted: false,
          requestedByCaptain: undefined,
          modifiedByCaptain: undefined,
          profileId: undefined,
          page: 0,
          size: 15,
          sortColumn: "dueDate",
          sortDirection: "asc",
        });
        break;
      }
      case "completedTrainingOrders": {
        setTrainingOrderSearchParams({
          assigneeId: "",
          rating: undefined,
          compatibleWithRating: undefined,
          trainingOrderId: "",
          scheduled: undefined,
          completed: true,
          profileId: undefined,
          deleted: false,
          requestedByCaptain: undefined,
          modifiedByCaptain: undefined,
          page: 0,
          size: 15,
          sortColumn: "dueDate",
          sortDirection: "asc",
        });
        break;
      }
      case "deletedTrainingOrders": {
        setTrainingOrderSearchParams({
          assigneeId: "",
          rating: undefined,
          compatibleWithRating: undefined,
          trainingOrderId: "",
          scheduled: undefined,
          completed: undefined,
          profileId: undefined,
          deleted: true,
          requestedByCaptain: undefined,
          modifiedByCaptain: undefined,
          page: 0,
          size: 15,
          sortColumn: "dueDate",
          sortDirection: "asc",
        });
        break;
      }
      case "requestedTrainingOrders": {
        setTrainingOrderSearchParams({
          assigneeId: "",
          rating: undefined,
          compatibleWithRating: undefined,
          trainingOrderId: "",
          scheduled: undefined,
          completed: undefined,
          deleted: false,
          requestedByCaptain: true,
          modifiedByCaptain: undefined,
          profileId: undefined,
          page: 0,
          size: 15,
          sortColumn: "dueDate",
          sortDirection: "asc",
        });
        break;
      }
    }

    if (newTab !== "activeTrainingOrders") {
      setAssignee(null);
    }
    setStatus([]);
    setRating(null);
    setProfile(null);

    setCurrentTab(newTab);
  };

  if (isSpecialistsLoading || isManagersLoading || isEvalSpecialistsLoading || isTrainingOrdersLoading) {
    return (
      <Box sx={{ display: "flex" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div>
      <div className="page-header tab-header">
        <div className="flex align-items-center g-3">
          <h1>Training Orders</h1>
          {canCreate && (
            <Button
              variant="contained"
              className="blue-button h-4"
              onClick={redirectToTrainingOrder}
            >
              New Training Order
            </Button>
          )}
        </div>
        <Tabs
          value={currentTab}
          onChange={(e, newTab) => handleActiveTabToggle(newTab)}
          aria-label="Training Order Listing Page Tabs"
          className="top-right-tabs"
        >
          <Tab
            value="requestedTrainingOrders"
            label={
              requestedOrdersCount > 0 ? (
                <Badge
                  color="error"
                  variant="dot"
                  overlap="rectangular"
                  anchorOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  Requested
                </Badge>
              ) : (
                "Requested"
              )
            }
          />
          <Tab value="activeTrainingOrders" label="Active" />
          <Tab value="scheduledTrainingOrders" label="Scheduled" />
          <Tab value="completedTrainingOrders" label="Completed" />
          <Tab value="deletedTrainingOrders" label="Deleted" />
        </Tabs>
      </div>
      <div className="filters-wrap">
        <div className="filters-list job-orders-filters">
          <label className="large-filter" style={{ width: "180px" }}>
            <span>Assignee:</span>
            <Autocomplete
              id="Assignee"
              value={assignee}
              options={assignees}
              onChange={(event, value) => setAssignee(value)}
              renderInput={(params) => <TextField {...params} />}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              getOptionLabel={(opt) => opt.name || opt.email}
            />
          </label>
          <label className="large-filter" style={{ width: "240px" }}>
            <span>Mariner</span>
            <MarinerProfileAutocomplete
              id="marinerAutocomplete"
              value={profile}
              marinerRating={undefined}
              onChange={(p) =>
                p === undefined ? setProfile(null) : setProfile(p)
              }
            />
          </label>
          <label>
            <span>Rating:</span>
            <Autocomplete
              id="Rating"
              options={Object.keys(JobRating).filter(
                (rating) =>
                  !DisabledJobRatings.has(JobRating[rating as JobRating])
              )}
              getOptionLabel={(option) => JobRatingLabel[option as JobRating]}
              value={rating}
              onChange={(event, value: any) => {
                setRating(JobRating[value as JobRating]);
              }}
              renderInput={(params) => <TextField {...params} />}
            />
          </label>
          <label className="small-filter" style={{ width: "200px" }}>
            <span>Training Order ID:</span>
            <OutlinedInput
              value={trainingOrderSearchParams.trainingOrderId || ""}
              onChange={(e) =>
                setTrainingOrderSearchParams({
                  ...trainingOrderSearchParams,
                  trainingOrderId: e.target.value,
                })
              }
              startAdornment={
                <InputAdornment position="start">T-</InputAdornment>
              }
              type="text"
            />
          </label>
          {!!currentTab && currentTab === "activeTrainingOrders" && (
            <label className="small-filter">
              <span>Status:</span>
              <Select
                id="status"
                value={status}
                onChange={(e) => {
                  const {
                    target: { value },
                  } = e;
                  const status =
                    typeof value === "string" ? value.split(",") : value;
                  if (status.includes("")) {
                    setStatus([]);
                  } else {
                    setStatus(status);
                  }
                }}
                multiple
              >
                <MenuItem value={"modified"}>Modified By Captain</MenuItem>
                <MenuItem value={""}>Show All</MenuItem>
              </Select>
            </label>
          )}
          <Button
            variant="contained"
            className="blue-button"
            onClick={resetTrainingOrderSearchParams}
          >
            Reset
          </Button>
        </div>
        <TableContainer className="job-order-table">
          <Table className="table">
            <TableHead>
              <TableRow>
                <TableCell align="center">Assignee</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={trainingOrderSearchParams.sortColumn === "id"}
                    direction={
                      trainingOrderSearchParams.sortColumn === "id"
                        ? trainingOrderSearchParams.sortDirection
                        : "asc"
                    }
                    onClick={handleSort("id")}
                  >
                    Training Order ID
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={trainingOrderSearchParams.sortColumn === "dueDate"}
                    direction={
                      trainingOrderSearchParams.sortColumn === "dueDate"
                        ? trainingOrderSearchParams.sortDirection
                        : "asc"
                    }
                    onClick={handleSort("dueDate")}
                  >
                    Training Needed Date
                  </TableSortLabel>
                </TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={trainingOrderSearchParams.sortColumn === "training"}
                    direction={
                      trainingOrderSearchParams.sortColumn === "training"
                        ? trainingOrderSearchParams.sortDirection
                        : "asc"
                    }
                    onClick={handleSort("training")}
                  >
                    Training Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={trainingOrderSearchParams.sortColumn === "rating"}
                    direction={
                      trainingOrderSearchParams.sortColumn === "rating"
                        ? trainingOrderSearchParams.sortDirection
                        : "asc"
                    }
                    onClick={handleSort("rating")}
                  >
                    Rating
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={trainingOrderSearchParams.sortColumn === "mariner"}
                    direction={
                      trainingOrderSearchParams.sortColumn === "mariner"
                        ? trainingOrderSearchParams.sortDirection
                        : "asc"
                    }
                    onClick={handleSort("mariner")}
                  >
                    Mariner
                  </TableSortLabel>
                </TableCell>
                <TableCell>Progress</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {trainingOrders?.map((row, index) => {
                return (
                  <TableRow key={"trainingOrder" + index}>
                    <TableCell className="centered">
                      <Tooltip
                        title={row.assignee?.name || row.assignee?.email || "Deleted user"}
                        placement="right"
                      >
                        <Avatar
                          sx={{
                            bgcolor: colorFromName(row.assignee?.name || ""),
                            height: 30,
                            width: 30,
                          }}
                        >
                          {!!row.assignee?.name
                            ? row.assignee.name.substring(0, 1)
                            : undefined}
                        </Avatar>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {isViewOnly ? (
                        <p>T-{row.id}</p>
                      ) : (
                        <Link
                          className="blue-link"
                          to={`/trainingOrders/${row.id}`}
                        >
                          T-{row.id}
                        </Link>
                      )}
                    </TableCell>
                    <TableCell>
                      {!!row.dueDate &&
                        formatDateString(row.dueDate.toString())}{" "}
                      {!!currentTab &&
                        currentTab === "requestedTrainingOrders" &&
                        !!row.requestByCaptain && (
                          <span className="critical-flag bg-navy">R</span>
                        )}
                      {!!currentTab &&
                        currentTab === "activeTrainingOrders" &&
                        !!row.modifiedByCaptain && (
                          <span className="critical-flag bg-grey">M</span>
                        )}
                    </TableCell>
                    <TableCell>
                      {!!row.startDate &&
                        formatDateString(row.startDate.toString())}{" "}
                    </TableCell>
                    <TableCell>
                      {!!row.endDate &&
                        formatDateString(row.endDate.toString())}{" "}
                    </TableCell>
                    <TableCell>{row.trainingName}</TableCell>
                    <TableCell>
                      <Tooltip
                        title={JobRatingLabel[row.rating as JobRating]}
                        placement="right"
                      >
                        <span>{row.rating.substring(1)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{row.mariner}</TableCell>
                    <TableCell>
                      <span className="progress-cell">
                        <LinearProgress
                          variant="determinate"
                          color="success"
                          sx={{ height: "8px" }}
                          value={
                            row.tasks !== 0
                              ? ((row.completed + row.notApplicable) * 100) /
                              row.tasks
                              : 0
                          }
                        />
                        {row.completed + row.notApplicable + "/" + row.tasks}
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
      <TablePagination
        component={"div"}
        count={(trainingOrdersFromApi?.totalElements as number) || 0}
        onPageChange={(event, newPage) =>
          setTrainingOrderSearchParams({
            ...trainingOrderSearchParams,
            page: newPage,
          })
        }
        rowsPerPageOptions={[15, 25, 50, 100]}
        page={trainingOrderSearchParams?.page}
        rowsPerPage={trainingOrderSearchParams?.size}
        onRowsPerPageChange={(event) =>
          setTrainingOrderSearchParams({
            ...trainingOrderSearchParams,
            size: parseInt(event.target.value),
            page: 0,
          })
        }
      />
    </div>
  );
};

export default TrainingOrderListComponent;
